import { useState } from "react";
import { Link } from "react-router-dom";
import { useCookieConsent } from "../context/CookieConsentContext";
import LiquidGlassButton from "./LiquidGlassButton";
import LiquidGlass from "./LiquidGlass";

/* ─── Toggle switch ─────────────────────────────────────────────────────────── */
function Toggle({ checked, onChange, disabled }) {
  return (
    <button
      className={`cookie-toggle${checked ? " is-checked" : ""}`}
      role="switch"
      aria-checked={checked}
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      style={{
        opacity: disabled ? .5 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      <span className="cookie-toggle-thumb" />
    </button>
  );
}

/* ─── Preferences modal ─────────────────────────────────────────────────────── */
function PreferencesModal({ onSave, onClose }) {
  const [analytics, setAnalytics] = useState(false);

  return (
    <div
      onClick={onClose}
      className="cookie-modal"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
    >
      <LiquidGlass
        onClick={e => e.stopPropagation()}
        className="cookie-modal-panel glass-panel"
        tone="light"
        cornerRadius={20}
        style={{
          padding: "32px 28px",
          maxWidth: 460,
          width: "100%",
          borderRadius: 20,
        }}
      >
        <h2 style={{ fontSize: 20, fontWeight: 700, color: "var(--text-primary)", letterSpacing: "-.02em", marginBottom: 6 }}>
          Cookie preferences
        </h2>
        <p style={{ fontSize: 13, color: "var(--text-dim)", lineHeight: 1.6, marginBottom: 24 }}>
          Choose whether to allow optional analytics. Essential browser storage remains on for sign-in and preferences.{" "}
          <Link to="/cookie" onClick={onClose} style={{ color: "var(--accent)", textDecoration: "none" }}>
            Cookie Policy
          </Link>
        </p>

        {/* Essential row */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 20 }}>
          <Toggle checked={true} onChange={() => {}} disabled />
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)", marginBottom: 2 }}>Essential</div>
            <div style={{ fontSize: 12, color: "var(--text-dim)", lineHeight: 1.5 }}>
              Account sign-in, consent choices, and theme preferences.
            </div>
          </div>
        </div>

        {/* Analytics row */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 32 }}>
          <Toggle checked={analytics} onChange={setAnalytics} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)", marginBottom: 2 }}>Analytics</div>
            <div style={{ fontSize: 12, color: "var(--text-dim)", lineHeight: 1.5 }}>
              Save your preference for optional analytics. The current website does not load an optional analytics tracker.
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <LiquidGlassButton
            onClick={() => onSave({ analytics })}
            width={190}
            height={42}
            tone="light"
            fallbackClassName="btn btn-sm btn-white"
          >
            Save preferences
          </LiquidGlassButton>
          <LiquidGlassButton
            onClick={onClose}
            width={112}
            height={42}
            tone="light"
            fallbackClassName="btn btn-sm btn-ghost"
          >
            Cancel
          </LiquidGlassButton>
        </div>
      </LiquidGlass>
    </div>
  );
}

/* ─── Main banner ────────────────────────────────────────────────────────────── */
export default function CookieBanner() {
  const { bannerOpen, closeBanner, acceptAll, rejectAll, saveConsent } = useCookieConsent();
  const [showPrefs, setShowPrefs] = useState(false);

  if (!bannerOpen) return null;

  if (showPrefs) {
    return (
      <PreferencesModal
        onSave={(prefs) => { saveConsent(prefs); setShowPrefs(false); }}
        onClose={() => setShowPrefs(false)}
      />
    );
  }

  return (
    <LiquidGlass
      style={{
        position: "fixed",
        bottom: 20, left: "50%",
        transform: "translateX(-50%)",
        width: "calc(100% - 32px)",
        maxWidth: 680,
        zIndex: 9998,
        borderRadius: 16,
        padding: "18px 20px",
        display: "flex",
        alignItems: "center",
        gap: 16,
        flexWrap: "wrap",
        animation: "slideUp .3s ease",
      }}
      className="cookie-banner glass-popup"
      tone="dark"
      cornerRadius={16}
    >
      <p style={{ flex: "1 1 200px", fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.55, margin: 0 }}>
        We use browser storage for sign-in and preferences. You can also save your choice about optional analytics.{" "}
        <Link to="/cookie" onClick={closeBanner} style={{ color: "var(--accent)", textDecoration: "none", whiteSpace: "nowrap" }}>
          Cookie Policy
        </Link>
      </p>

      <div style={{ display: "flex", gap: 8, flexShrink: 0, flexWrap: "wrap" }}>
        <LiquidGlassButton
          onClick={() => setShowPrefs(true)}
          width={82}
          height={34}
          tone="dark"
          fallbackClassName="btn btn-sm btn-ghost"
        >
          Manage
        </LiquidGlassButton>
        <LiquidGlassButton
          onClick={rejectAll}
          width={92}
          height={34}
          tone="dark"
          fallbackClassName="btn btn-sm btn-ghost"
        >
          Reject all
        </LiquidGlassButton>
        <LiquidGlassButton
          onClick={acceptAll}
          width={90}
          height={34}
          tone="dark"
          fallbackClassName="btn btn-sm btn-white"
        >
          Accept all
        </LiquidGlassButton>
      </div>
    </LiquidGlass>
  );
}
